// src/pages/admin/invoices/CustomerBrandSheet.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardBody } from "@material-tailwind/react";
import { useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query as qy,
  orderBy,
  limit as limitFn,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";
import logo from "../../images/2.png";
import FancyArabicLabel from "./FancyArabicLabel";

/* ----------------------- إعدادات الهوية البصرية ----------------------- */
const BRAND = {
  brown: "#C15E2A",
  orange: "#F79F2D",
  sand: "#FFEEDC",
  dark: "#3C261C",
};

/* ----------------------------- ثوابت منطق العرض ----------------------------- */
const TEN_MIN_MS = 10 * 60 * 1000; // 10 دقائق: نافذة الفواتير المقبولة
const SHOW_MS = 10_000; // 10 ثوانٍ لكل اسم
const FETCH_LIMIT = 50; // نجلب أحدث 50 فاتورة ونرشّح محليًا
const REFRESH_WINDOW_MS = 15_000; // نعيد الحساب دوريًا لإسقاط الأسماء المنتهية

export default function CustomerBrandSheet() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);

  // اسم احتياطي اختياري عبر URL ?name=...
  const optionalName = useMemo(
    () => (params.get("name") || "").trim(),
    [params]
  );

  const [uid, setUid] = useState(null);

  // نخزّن اللقطات الخام (createdAtMs + customerName) من السناپشوت
  const [rawDocs, setRawDocs] = useState([]);
  const [names, setNames] = useState([]); // الأسماء ضمن آخر 10 دقائق فقط (بعد إزالة التكرارات وبحد أقصى 10)
  const [idx, setIdx] = useState(0); // مؤشر الاسم الحالي

  /* ------------------------- جلب uid (Auth + بدائل) ------------------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
    return () => unsub();
  }, []);

  // fallback لاستخراج uid عندما تكون الصفحة خارج التخطيط (أو لم تُفعَّل Auth بعد)
  useEffect(() => {
    if (uid) return; // لدينا uid بالفعل
    const uidFromQuery = params.get("uid");
    const uidFromLS =
      localStorage.getItem("uid") ||
      localStorage.getItem("userUid") ||
      localStorage.getItem("userId");
    if (uidFromQuery) setUid(uidFromQuery);
    else if (uidFromLS) setUid(uidFromLS);
  }, [uid, params]);

  /* ----------------------------- اشتراك لحظي ----------------------------- */
  useEffect(() => {
    if (!uid) return;

    const invoicesRef = collection(db, "users", uid, "invoices");
    const q = qy(
      invoicesRef,
      orderBy("createdAt", "desc"),
      limitFn(FETCH_LIMIT)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((doc) => {
          const data = doc.data() || {};
          let createdAtMs = 0;

          // يدعم Timestamp و {seconds}
          if (data?.createdAt?.toDate) {
            createdAtMs = data.createdAt.toDate().getTime();
          } else if (typeof data?.createdAt?.seconds === "number") {
            createdAtMs = data.createdAt.seconds * 1000;
          }

          const customerName = String(data.customerName || "").trim();
          // نخزّن حتى لو createdAtMs=0 (سنُسقِط لاحقًا)
          arr.push({ createdAtMs, customerName });
        });
        setRawDocs(arr);
      },
      // لا نكسر الصفحة عند الخطأ؛ فقط نُبقي الحالة كما هي
      () => {}
    );

    return () => unsub();
  }, [uid]);

  /* --------- دالة تعيد حساب أسماء آخر 10 دقائق + إزالة تكرارات + حد أقصى 10 --------- */
  const recomputeNames = useCallback(() => {
    const now = Date.now();

    const within10min = rawDocs
      .filter((d) => d.createdAtMs && now - d.createdAtMs <= TEN_MIN_MS)
      .map((d) => d.customerName)
      .filter(Boolean);

    const deduped = [];
    const seen = new Set();
    for (const n of within10min) {
      if (!seen.has(n)) {
        seen.add(n);
        deduped.push(n);
      }
      if (deduped.length >= 10) break;
    }

    if (!deduped.length && optionalName) {
      setNames([optionalName]);
      setIdx(0);
      return;
    }

    setNames(deduped);
    // تأكد أن المؤشر لا يخرج عن الطول الجديد
    setIdx((i) => (deduped.length ? i % deduped.length : 0));
  }, [rawDocs, optionalName]);

  // أعِد الحساب عند وصول سناپشوت جديد
  useEffect(() => {
    recomputeNames();
  }, [rawDocs, recomputeNames]);

  // مؤقّت تنظيف: حتى لو ما صار تغيير في Firestore، نُسقِط الأسماء القديمة بعد انتهاء 10 دقائق
  useEffect(() => {
    const id = setInterval(() => {
      recomputeNames();
    }, REFRESH_WINDOW_MS);
    return () => clearInterval(id);
  }, [recomputeNames]);

  /* ------------------------- تدوير الاسم كل 10 ثوانٍ ------------------------- */
  useEffect(() => {
    if (names.length <= 1) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % names.length);
    }, SHOW_MS);
    return () => clearInterval(id);
  }, [names]);

  // اسم العرض الحالي: إن لم يوجد اسم في الوقت المحدد → "يورنيم"
  const currentLabel = names.length ? names[idx] : optionalName || "يورنيم";

  /* ---------------------------------- واجهة --------------------------------- */
  return (
    <div dir="rtl" className="bg-[#f79f2d] ">
      {/* متغيرات CSS للألوان */}
      <style>{`
        :root{
          --brand-brown:${BRAND.brown};
          --brand-orange:${BRAND.orange};
          --brand-sand:${BRAND.sand};
          --brand-dark:${BRAND.dark};
          --bg:${BRAND.sand};
        }
      `}</style>

      <div className="mx-auto max-w-8xl ">
        <div className="grid grid-cols-1 ">
          <Card className="shadow-none overflow-hidden bg-[#f79f2d]">
            <CardBody className="p-0 ">
              <div className="h-64 md:h-[700px] flex items-center justify-center relative">
                <img
                  src={logo}
                  alt="شعار فطيرة وكرك"
                  className="h-[600px] drop-shadow"
                />

                <div className="relative">
                  <FancyArabicLabel
                    text={currentLabel}
                    width={280}
                    rotate={-12}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
